import React, { useState } from 'react';
import { sePraIntervalo } from './utils/semanaEpi';

// Quadro de Avisos de Divergencias (Bloco F1, 23/09/2026 - Walber).
// Componente SO de exibicao: nao chama a API e nao grava nada. Recebe a resposta
// de GET /api/fad01/conferencia-laboratorio (ja juntada pela tela que usa).
// Cores fixas: o TOVIS nao tem modo escuro. Fica fora da impressao pela classe
// recebida em classeNoPrint (o Quadro de Positividade passa "qp-noprint").

export interface ItemMural {
  regra: string; mensagem: string;
  titulo?: string; porque?: string; causas?: string; corrigir?: string;
}
export interface BoletimMural {
  id: number; data: string; agente: string; quarteirao_texto: string | null;
  amostras_coletadas: number; sem_resultado: number; congeladas: number;
  tubitos_sem_resultado: number[]; travas: ItemMural[]; avisos: ItemMural[];
}
export interface GrupoMural {
  area_id: number; area_nome: string; se: number;
  soma_imoveis_aeg: number; soma_depositos_aeg: number;
  soma_imoveis_alb: number; soma_depositos_alb: number;
  recusa_sispncd: boolean; boletins: BoletimMural[];
  boletins_conferidos: number; amostras_coletadas: number; sem_resultado: number; congeladas: number;
}
export interface AmostrasResumo { coletadas: number; sem_resultado: number; congeladas: number; }
export interface TotaisAuditoria {
  travas: number; avisos: number; boletins_com_trava: number; boletins_com_aviso: number;
  boletins_com_amostra_sem_resultado: number; semanas_com_recusa: number;
}
export interface Explicacao { titulo?: string; porque?: string; causas?: string; corrigir?: string; }
export interface RespostaAuditoria {
  grupos: GrupoMural[];
  auditado_em: string;
  boletins_conferidos: number;
  amostras: AmostrasResumo;
  totais: TotaisAuditoria;
  explicacao_amostra: Explicacao;
}
export type EstadoAuditoria = 'carregando' | 'ok' | 'falhou' | 'sem_area';
type Nivel = 'verde' | 'amarelo' | 'vermelho' | 'cinza';

interface Props {
  estado: EstadoAuditoria;
  resposta: RespostaAuditoria | null;
  amostrasQuadro: AmostrasResumo | null;
  onRefazer: () => void;
  onAbrirFAD01?: (id: number) => void;   // sem ela, o botao "Abrir boletim" nao aparece
  classeNoPrint: string;
  tentativaEm?: string;                  // ISO da ultima tentativa (texto do selo cinza)
}

const COR: Record<Nivel, { forte: string; fundo: string; texto: string }> = {
  vermelho: { forte: '#C62828', fundo: '#FDECEC', texto: '#8E1B1B' },
  amarelo: { forte: '#A86B00', fundo: '#FFF6DC', texto: '#6B4400' },
  verde: { forte: '#1E7A4C', fundo: '#E6F4EC', texto: '#14553A' },
  cinza: { forte: '#5B6778', fundo: '#EEF1F5', texto: '#3C4757' },
};
const TINTA = '#122033';
const SUB = '#4A5A70';
const BORDA = '#D5DFEA';
const LINK = '#0B4F8A';

const CONFERIDO = [
  'Imóveis positivos ≤ depósitos positivos, por espécie (regra do SISPNCD)',
  'Depósito positivo sempre com imóvel positivo, e vice-versa',
  'Depósitos positivos ≤ depósitos inspecionados, tipo a tipo',
  'Imóveis positivos ≤ imóveis trabalhados e inspecionados',
  'Tubitos coletados × depósitos positivos',
  'Exemplares contados × depósitos positivos',
  'Quarteirões positivos × depósitos e quarteirões trabalhados',
  'Resultado do Laboratório × verso do FAD-01',
  'Todas as amostras com resultado',
];

const plural = (n: number, um: string, varios: string) => (n === 1 ? um : varios);
const ddmmaaaa = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const hhmm = (iso?: string) => {
  if (!iso) return '--:--';
  const m = iso.match(/T(\d{2}):(\d{2})/);   // hora do servidor, como veio no ISO
  return m ? `${m[1]}:${m[2]}` : '--:--';
};
const dois = (n: number) => String(n).padStart(2, '0');
function periodoSe(se: number): string {
  const { inicio, fim } = sePraIntervalo(se);
  return `${dois(inicio.getDate())}/${dois(inicio.getMonth() + 1)} a `
    + `${dois(fim.getDate())}/${dois(fim.getMonth() + 1)}/${fim.getFullYear()}`;
}

// ── Icones (SVG inline, sem emoji) ─────────────────────────────────────────
function IconeNivel({ nivel, tam = 28 }: { nivel: Nivel; tam?: number }) {
  const p = { width: tam, height: tam, viewBox: '0 0 24 24', fill: 'none', stroke: '#ffffff', strokeWidth: 3,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  if (nivel === 'verde') return <svg {...p}><polyline points="5 12.5 10 17.5 19 7" /></svg>;
  if (nivel === 'vermelho') return <svg {...p}><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>;
  if (nivel === 'amarelo') {
    return (
      <svg {...p} strokeWidth={2.4}>
        <path d="M12 3.5 L21.5 20 H2.5 Z" />
        <line x1="12" y1="10" x2="12" y2="14" /><line x1="12" y1="17" x2="12" y2="17.2" />
      </svg>
    );
  }
  return (
    <svg {...p} strokeWidth={2.4}>
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="7.5" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12" y2="16.7" />
    </svg>
  );
}
function IconeCheck({ cor, tam = 18 }: { cor: string; tam?: number }) {
  return (
    <svg width={tam} height={tam} viewBox="0 0 24 24" fill="none" stroke={cor} strokeWidth={3}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
      <polyline points="5 12.5 10 17.5 19 7" />
    </svg>
  );
}
function IconeAbrir() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
      <path d="M14 4h6v6" /><line x1="20" y1="4" x2="11" y2="13" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
  );
}

// ── Estilos ────────────────────────────────────────────────────────────────
const ROTULO: React.CSSProperties = {
  fontSize: 12, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: SUB,
};
const BOTAO: React.CSSProperties = {
  minHeight: 44, padding: '8px 16px', borderRadius: 8, border: `2px solid ${TINTA}`, background: '#ffffff',
  color: TINTA, fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
};
const CSS_GIRO = '@keyframes tovisMuralGiro { to { transform: rotate(360deg); } }';

// ── Bloco de um item (trava, aviso ou amostra) ────────────────────────────
function BlocoItem({ nivel, etiqueta, regra, titulo, encontrou, exp }: {
  nivel: Nivel; etiqueta: string; regra: string; titulo: string; encontrou: string; exp: Explicacao;
}) {
  const c = COR[nivel];
  const campo = (rotulo: string, texto: React.ReactNode) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ ...ROTULO, color: c.texto }}>{rotulo}</span>
      <span style={{ fontSize: 15, color: TINTA, lineHeight: 1.45 }}>{texto || '—'}</span>
    </div>
  );
  return (
    <div style={{ background: c.fundo, borderLeft: `6px solid ${c.forte}`, borderRadius: 8, padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ background: c.forte, color: '#ffffff', fontSize: 12, fontWeight: 800, letterSpacing: 0.6,
          padding: '3px 8px', borderRadius: 4 }}>{etiqueta}</span>
        <span style={{ fontSize: 16, fontWeight: 800, color: c.texto, flex: '1 1 240px' }}>{titulo}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: c.texto, border: `1.5px solid ${c.forte}`,
          borderRadius: 999, padding: '2px 10px', background: '#ffffff' }}>{regra}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px 20px' }}>
        {campo('O que o sistema encontrou', <strong>{encontrou}</strong>)}
        {campo('Por que chama atenção', exp.porque)}
        {campo('Causas mais comuns', exp.causas)}
        {campo('Como corrigir', exp.corrigir)}
      </div>
    </div>
  );
}

// ── Componente ─────────────────────────────────────────────────────────────
export default function MuralDivergencias({
  estado, resposta, amostrasQuadro, onRefazer, onAbrirFAD01, classeNoPrint, tentativaEm,
}: Props) {
  const [aberto, setAberto] = useState(true);

  if (estado === 'sem_area') {
    return (
      <div className={classeNoPrint} style={{ background: COR.cinza.fundo, border: `2px solid ${BORDA}`, borderRadius: 12,
        padding: '12px 16px', marginBottom: 14, color: COR.cinza.texto, fontSize: 15, fontWeight: 700 }}>
        Escolha uma área para a auditoria rodar
      </div>
    );
  }

  const falhou = estado === 'falhou' || (estado === 'ok' && !resposta);
  const carregando = estado === 'carregando';
  const r = !falhou && !carregando ? resposta : null;
  const travas = r?.totais.travas ?? 0;
  const avisos = r?.totais.avisos ?? 0;
  const recusas = r?.totais.semanas_com_recusa ?? 0;
  const pendentes = Math.max(r?.amostras.sem_resultado ?? 0, amostrasQuadro?.sem_resultado ?? 0);
  const erros = travas + recusas;

  let nivel: Nivel;
  if (falhou || carregando) nivel = 'cinza';
  else if (erros > 0 || pendentes > 0) nivel = 'vermelho';
  else if (avisos > 0) nivel = 'amarelo';
  else nivel = 'verde';
  const c = COR[nivel];

  let titulo: string;
  let detalhe: string;
  if (carregando) {
    titulo = 'Auditando…';
    detalhe = 'Conferindo os boletins deste filtro';
  } else if (falhou) {
    titulo = 'Auditoria indisponível — dados não conferidos';
    detalhe = `Tentativa às ${hhmm(tentativaEm)} · o sistema não respondeu`;
  } else {
    if (nivel === 'verde') titulo = 'Tudo certo — nenhuma divergência';
    else if (nivel === 'amarelo') titulo = `${avisos} ${plural(avisos, 'ponto', 'pontos')} para conferir — nenhum erro`;
    else if (erros > 0) titulo = `${erros} ${plural(erros, 'erro encontrado', 'erros encontrados')} — não transcreva ainda`;
    else titulo = `${pendentes} ${plural(pendentes, 'amostra sem resultado', 'amostras sem resultado')} — não transcreva ainda`;
    detalhe = `Conferida às ${hhmm(r?.auditado_em)} · ${r?.boletins_conferidos ?? 0} `
      + `${plural(r?.boletins_conferidos ?? 0, 'boletim', 'boletins')} · ${r?.amostras.coletadas ?? 0} `
      + `${plural(r?.amostras.coletadas ?? 0, 'amostra', 'amostras')}`;
  }

  // ── A1) Selo ──
  const selo = (
    <div style={{ background: c.fundo, border: `2px solid ${c.forte}`, borderRadius: 14, padding: '14px 16px',
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 12 }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: c.forte, flex: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {carregando ? (
          <span aria-label="Auditando" style={{ width: 28, height: 28, borderRadius: '50%',
            border: '4px solid rgba(255,255,255,0.35)', borderTopColor: '#ffffff',
            animation: 'tovisMuralGiro 0.9s linear infinite', display: 'block' }} />
        ) : <IconeNivel nivel={nivel} />}
      </div>
      <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ ...ROTULO, color: c.texto }}>Auditoria do módulo realizada</span>
        <span style={{ fontSize: 22, fontWeight: 800, color: c.texto, lineHeight: 1.2 }}>{titulo}</span>
        <span style={{ fontSize: 15, color: SUB }}>{detalhe}</span>
      </div>
      <button type="button" style={BOTAO} onClick={onRefazer} disabled={carregando}>Refazer auditoria</button>
    </div>
  );

  // ── A2) Mural ──
  const contador = (n: number, rotulo: string, cor: Nivel) => {
    const k = n > 0 ? COR[cor] : COR.cinza;
    return (
      <div style={{ background: k.fundo, border: `1.5px solid ${n > 0 ? k.forte : BORDA}`, borderRadius: 10,
        padding: '6px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 96 }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: n > 0 ? k.forte : COR.cinza.forte }}>{n}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: k.texto, textAlign: 'center' }}>{rotulo}</span>
      </div>
    );
  };

  const grupos = [...(r?.grupos ?? [])].sort((a, b) => a.se - b.se || a.area_nome.localeCompare(b.area_nome));
  const temItens = grupos.length > 0;
  const expAmostra = r?.explicacao_amostra ?? {};

  const corpoGrupos = grupos.map(g => {
    const imov = g.soma_imoveis_aeg + g.soma_imoveis_alb;
    const dep = g.soma_depositos_aeg + g.soma_depositos_alb;
    const partesRecusa: string[] = [];
    if (g.soma_imoveis_aeg > g.soma_depositos_aeg)
      partesRecusa.push(`Ae. aegypti ${g.soma_imoveis_aeg} imóveis × ${g.soma_depositos_aeg} depósitos`);
    if (g.soma_imoveis_alb > g.soma_depositos_alb)
      partesRecusa.push(`Ae. albopictus ${g.soma_imoveis_alb} imóveis × ${g.soma_depositos_alb} depósitos`);
    return (
      <section key={`${g.area_id}-${g.se}`} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ borderBottom: `3px solid ${TINTA}`, paddingBottom: 6, display: 'flex', flexWrap: 'wrap',
          alignItems: 'baseline', gap: '4px 14px' }}>
          <span style={{ fontSize: 20, fontWeight: 800, color: TINTA }}>SE {dois(g.se)}</span>
          <span style={{ fontSize: 15, color: SUB, fontWeight: 600 }}>{periodoSe(g.se)}</span>
          <span style={{ fontSize: 16, fontWeight: 800, color: TINTA }}>{g.area_nome}</span>
          {!g.recusa_sispncd && (
            <span style={{ marginLeft: 'auto', fontSize: 14, color: SUB }}>
              Semana não será recusada pelo SISPNCD ({imov} {plural(imov, 'imóvel', 'imóveis')} × {dep} {plural(dep, 'depósito positivo', 'depósitos positivos')})
            </span>
          )}
        </div>
        {g.recusa_sispncd && (
          <div style={{ background: COR.vermelho.forte, color: '#ffffff', borderRadius: 8, padding: '10px 14px',
            fontSize: 15, fontWeight: 800 }}>
            Esta semana será recusada no SISPNCD: {partesRecusa.join(' · ')}
          </div>
        )}
        {g.boletins.map(b => (
          <article key={b.id} style={{ border: `1.5px solid ${BORDA}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ background: '#E4EBF3', padding: '10px 14px', display: 'flex', flexWrap: 'wrap',
              alignItems: 'center', gap: '6px 16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 260px' }}>
                <span style={{ ...ROTULO }}>Boletim</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: TINTA }}>{ddmmaaaa(b.data)} · {b.agente || '—'}</span>
                <span style={{ fontSize: 14, color: SUB }}>nº {b.id} · quarteirão(ões) {b.quarteirao_texto || '—'}</span>
              </div>
              {onAbrirFAD01 && (
                <button type="button" onClick={() => onAbrirFAD01(b.id)}
                  style={{ ...BOTAO, border: `2px solid ${LINK}`, color: LINK }}>
                  <IconeAbrir /> Abrir boletim no FAD-01
                </button>
              )}
            </div>
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10, background: '#ffffff' }}>
              {b.travas.map((it, i) => (
                <BlocoItem key={`t${i}`} nivel="vermelho" etiqueta="ERRO · IMPEDE SALVAR" regra={`Regra ${it.regra}`}
                  titulo={it.titulo || it.mensagem} encontrou={it.mensagem} exp={it} />
              ))}
              {b.avisos.map((it, i) => (
                <BlocoItem key={`a${i}`} nivel="amarelo" etiqueta="CONFERIR" regra={`Regra ${it.regra}`}
                  titulo={it.titulo || it.mensagem} encontrou={it.mensagem} exp={it} />
              ))}
              {b.sem_resultado > 0 && (
                <BlocoItem nivel="vermelho" etiqueta="SEM RESULTADO" regra="Amostra"
                  titulo={expAmostra.titulo || 'Amostra ainda sem resultado'}
                  encontrou={`${b.sem_resultado} ${plural(b.sem_resultado, 'tubito', 'tubitos')} sem resultado: nº ${b.tubitos_sem_resultado.join(', ')}`}
                  exp={expAmostra} />
              )}
            </div>
          </article>
        ))}
      </section>
    );
  });

  const corpoVerde = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: COR.verde.forte, flex: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconeNivel nivel="verde" tam={24} />
        </div>
        <span style={{ fontSize: 18, fontWeight: 800, color: COR.verde.texto }}>
          A auditoria não encontrou divergências neste filtro
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '8px 20px' }}>
        {CONFERIDO.map(t => (
          <div key={t} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 15, color: TINTA }}>
            <IconeCheck cor={COR.verde.forte} /> <span>{t}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const corpoFalhou = (
    <div style={{ background: COR.cinza.fundo, borderRadius: 10, padding: '12px 14px', display: 'flex',
      flexDirection: 'column', gap: 6, color: COR.cinza.texto }}>
      <span style={{ fontSize: 18, fontWeight: 800 }}>Não foi possível fazer a auditoria agora</span>
      <span style={{ fontSize: 15 }}>
        O sistema não respondeu à conferência. Isso não quer dizer que está tudo certo: os dados não foram conferidos.
      </span>
      <span style={{ fontSize: 15, fontWeight: 700 }}>Clique em Refazer auditoria. Se continuar cinza, não transcreva.</span>
    </div>
  );

  let corpo: React.ReactNode;
  if (carregando) corpo = <span style={{ fontSize: 15, color: SUB }}>Auditando…</span>;
  else if (falhou) corpo = corpoFalhou;
  else if (temItens) corpo = <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>{corpoGrupos}</div>;
  else if (pendentes > 0) {
    // O Quadro contou amostra sem resultado que a conferencia nao listou: avisa, sem inventar boletim.
    corpo = (
      <BlocoItem nivel="vermelho" etiqueta="SEM RESULTADO" regra="Amostra"
        titulo={expAmostra.titulo || 'Amostra ainda sem resultado'}
        encontrou={`${pendentes} ${plural(pendentes, 'amostra', 'amostras')} sem resultado neste filtro`} exp={expAmostra} />
    );
  } else corpo = corpoVerde;

  return (
    <div className={classeNoPrint} style={{ marginBottom: 14 }}>
      <style>{CSS_GIRO}</style>
      {selo}
      <div style={{ background: '#ffffff', border: `1.5px solid ${BORDA}`, borderRadius: 14, padding: '14px 16px',
        display: 'flex', flexDirection: 'column', gap: 14, color: TINTA }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: 0.4 }}>QUADRO DE AVISOS DE DIVERGÊNCIAS</span>
            <span style={{ fontSize: 13, color: SUB }}>Segue os filtros abaixo · não aparece na impressão</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {contador(erros, plural(erros, 'erro', 'erros'), 'vermelho')}
            {contador(avisos, 'para conferir', 'amarelo')}
            {contador(pendentes, 'amostras sem resultado', 'vermelho')}
          </div>
          <button type="button" style={BOTAO} onClick={() => setAberto(a => !a)} aria-expanded={aberto}>
            {aberto ? 'Recolher ▴' : 'Mostrar ▾'}
          </button>
        </div>
        {aberto && corpo}
      </div>
    </div>
  );
}
